import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { Spacer, Text, Button } from '@zillow/constellation';
import { Container } from '@zillow/zrs-comet';

import { RMWContext } from 'app/shared/providers/RMWContext';
import { UserSelectors } from 'app/shared/selectors';
import api from 'app/shared/utils/api';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';

import { StyledContainer } from './StyledComponents';

const DEFAULT_PAGE_TITLE = 'Welcome to Rental Manager';
const {
    LOADING,
    ERROR,
    INITIAL,
    SUCCESS,
} = LoadingConstants;

const UserManagementInvitationPage = ({ dispatch, user }) => {
    const { paths } = React.useContext(RMWContext);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [loadingState, setLoadingState] = React.useState(INITIAL);
    const [companyInvite, setCompanyInvite] = React.useState(null);
    const [redirectTarget, setRedirectTarget] = React.useState(null);

    React.useEffect(() => {
        if (user.roles.includes(UserPermissionsConstants.PERMISSION_ROLES.COMPANY_ADMIN)) {
            setRedirectTarget(`${paths.userManagementPage}/${paths.userManagementUsers}`);
        } else if (user.roles.includes(UserPermissionsConstants.PERMISSION_ROLES.COMPANY_USER)) {
            setRedirectTarget(paths.propertiesPage);
        } else {
            setLoadingState(LOADING);
            dispatch(api.users.company.getUserManagementInvitation())
                .then((data) => {
                    setCompanyInvite(data.response);
                    setLoadingState(SUCCESS);
                })
                .catch(() => {
                    setErrorMessage('Failed to get invitation');
                    setLoadingState(ERROR);
                });
        }
    }, []);

    if (redirectTarget) return <Redirect to={redirectTarget} />;

    const acceptInvitation = () => {
        setLoadingState(LOADING);
        dispatch(UserManagementActions.onboardPendingUser())
            .then(() => {
                window.router.transitionTo(paths.propertiesPage);
            })
            .catch(() => {
                setLoadingState(ERROR);
            });
    };

    const declineInvitation = () => {
        setLoadingState(LOADING);
        dispatch(api.users.company.declineUserManagementInvitation())
            .then(() => {
                window.router.transitionTo(paths.propertiesPage);
            })
            .catch(() => {
                setErrorMessage('Failed to decline invitation');
                setErrorMessage(ERROR);
            });
    };

    const renderInvitation = () => (
        <>
            <Text as="div">
                You&apos;re invited to join&nbsp;
                <strong>{companyInvite.companyName}</strong>
                . By accepting, all the properties
                in your account will be added to the company and you will be
                granted access to company properties.
            </Text>
            <Spacer marginY="sm">
                <Text as="div">
                    If you don&apos;t want your properties to be added to the company,
                    please decline the invite, contact support at
                    rentalshelp@zillowgroup.com to remove unwanted properties from your
                    account and then request your company admin to invite you again later.
                </Text>
            </Spacer>
            <Spacer marginBottom="xs">
                <Button buttonType="primary" onClick={acceptInvitation}>
                    Accept Invitation
                </Button>
            </Spacer>
            <Button onClick={declineInvitation}>
                Decline Invitation
            </Button>
        </>
    );

    const renderInfo = () => {
        if (companyInvite.isInvitationFound) return renderInvitation();
        return <Text as="div">No invitation found at this time.</Text>;
    };

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div>
                <Container>
                    <StyledContainer>
                        <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                        <Spacer marginY="sm">
                            <Text fontType="h4">{DEFAULT_PAGE_TITLE}</Text>
                        </Spacer>
                        {loadingState === SUCCESS && renderInfo()}
                        {(loadingState === LOADING || loadingState === INITIAL) && <SpinnerContainer />}
                        {loadingState === ERROR && (errorMessage ? <Text as="div">{errorMessage}</Text>
                            : (
                                <>
                                    <Text as="div">Failed to load your settings.</Text>
                                    <Text as="div">
                                        Please refresh, or check with your
                                        company if you have an invitation.
                                    </Text>
                                </>
                            )
                        )}
                    </StyledContainer>
                </Container>
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
});

export default connect(mapStateToProps)(UserManagementInvitationPage);
